import React, { useMemo, useState } from "react";
import { appointmentRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, Card, Drawer, EmptyState, PageHead, StatusBadge, baht, formatDate, formatTime, useAsync } from "../../components/ui";

const DOW = ["อา", "จ", "อ", "พ", "พฤ", "ศ", "ส"];
const DOW_EN = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const sameDay = (a, b) => a.toDateString() === b.toDateString();

export default function Calendar() {
  const { orgId, branchId } = useApp();
  const { tx, lang } = useI18n();
  const [cursor, setCursor] = useState(startOfMonth(new Date()));
  const [selectedDay, setSelectedDay] = useState(null);
  const { data: appointments } = useAsync(() => appointmentRepo.all(orgId, (row) => !branchId || row.branchId === branchId), [orgId, branchId], []);

  const days = useMemo(() => {
    const first = startOfMonth(cursor);
    const startOffset = first.getDay();
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - startOffset);
    return Array.from({ length: 42 }).map((_, index) => {
      const date = new Date(gridStart);
      date.setDate(gridStart.getDate() + index);
      return date;
    });
  }, [cursor]);

  const eventsOf = (date) => (appointments || []).filter((row) => sameDay(new Date(row.startAt), date)).sort((a, b) => new Date(a.startAt) - new Date(b.startAt));

  const selectedEvents = selectedDay ? eventsOf(selectedDay) : [];

  return (
    <>
      <PageHead title={tx("ปฏิทินรวม", "Master calendar")} subtitle={tx("แสดงคิวทั้งหมดของทุกทรัพยากรและผู้ให้บริการ", "All bookings across resources and staff")} />

      <div className="cal-head">
        <Button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}>‹</Button>
        <div className="u-strong u-fs-18">{cursor.toLocaleDateString(lang === "en" ? "en-US" : "th-TH", { month: "long", year: "numeric" })}</div>
        <Button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}>›</Button>
        <Button onClick={() => setCursor(startOfMonth(new Date()))}>{tx("วันนี้", "Today")}</Button>
      </div>

      <div className="cal-grid">
        {(lang === "en" ? DOW_EN : DOW).map((label) => (
          <div key={label} className="cal-dow">{label}</div>
        ))}
        {days.map((date) => {
          const events = eventsOf(date);
          const isOut = date.getMonth() !== cursor.getMonth();
          const isToday = sameDay(date, new Date());
          return (
            <div key={date.toISOString()} className={`cal-cell ${isOut ? "is-out" : ""} ${isToday ? "is-today" : ""}`} onClick={() => setSelectedDay(date)}>
              <div className="cal-cell__date">{date.getDate()}</div>
              {events.slice(0, 3).map((event) => (
                <div key={event.id} className={`cal-event ${event.status === "completed" ? "is-done" : event.status === "cancelled" ? "is-cancel" : ""}`}>
                  {formatTime(event.startAt)} {event.customerName}
                </div>
              ))}
              {events.length > 3 ? <div className="u-fs-12 u-muted">+{events.length - 3} {tx("อื่น ๆ", "more")}</div> : null}
            </div>
          );
        })}
      </div>

      <Drawer open={!!selectedDay} title={selectedDay ? formatDate(selectedDay) : ""} onClose={() => setSelectedDay(null)}>
        {selectedEvents.length === 0 ? (
          <EmptyState text={tx("วันนี้ยังไม่มีคิว", "No appointments")} />
        ) : (
          selectedEvents.map((event) => (
            <Card key={event.id}>
              <div className="u-between">
                <div>
                  <div className="u-strong">{event.customerName}</div>
                  <div className="u-fs-13 u-muted">{event.serviceName}</div>
                </div>
                <StatusBadge status={event.status} />
              </div>
              <div className="u-between u-mt-8 u-fs-13">
                <span>{formatTime(event.startAt)} · {event.durationMin} {tx("นาที", "min")}</span>
                <span className="u-strong">{baht(event.price)}</span>
              </div>
            </Card>
          ))
        )}
      </Drawer>
    </>
  );
}
